// Adaptives Lernen fuer die TopBar-Suche (Schicht 2). Lernt aus
// (Such-Praefix -> ausgewaehltes Item) und gibt beim naechsten Mal
// einen gedeckelten Bonus, damit das richtige Item nach vorne rutscht.
//
// Designregeln:
//   - Decay-Score statt Treffer-Zaehler: konvergiert mathematisch
//     gegen 1/(1-DECAY); kann nie weglaufen. Bei DECAY=0.9 -> max 10.
//   - Bonus ist gedeckelt (BONUS_CAP), damit ein oft geklicktes Item
//     niemals einen echten Namens-Treffer ueberstimmen kann. Der
//     Lern-Effekt bricht Gleichstaende + hebt mittlere Treffer hoch,
//     verzerrt aber keine schlechten Matches nach oben.
//   - Truncation in record() statt separater GC: bei Ueberlauf wird
//     die schwaechste Bindung im Praefix gedroppt; bei Ueberlauf der
//     Praefix-Anzahl der schwaechste Praefix komplett. So bleibt der
//     serialisierte Blob unter dem 16-KB-Body-Limit der prefs-API.
//   - Query-Normalisierung: trim + lowercase + erstes Token. "task",
//     "tasks ", "Tasks foo" treffen denselben Slot ("task" als erster
//     Token-Praefix), waehrend "foo" sauber getrennt bleibt.
//   - Modul ist framework-frei (kein Svelte, kein DOM) -- der Store-
//     Wrapper (search-learn-store.svelte.ts) macht Persistenz + Reaktivitaet.
//
// Worst-case-Sizing bei den Default-Konstanten:
//   50 Praefixe x 10 Items x ~30 Byte JSON       ~15 KB
//   30 globale frecent-Items x ~30 Byte           ~ 1 KB
//                                          Summe ~16 KB <-- knapp am Limit.
// Die Defaults haelt der Store bewusst konservativ. User-Overrides via
// search_learn_config-Prefs werden auf sichere Bereiche geclamped.

export const STATE_VERSION = 1;

export interface LearnConfig {
  // Decay-Faktor pro Auswahl. Score konvergiert gegen 1/(1-decay).
  // 0.85 = vergisst schnell (max 6.67), 0.9 = stabil (max 10),
  // 0.95 = sehr stabil (max 20, traege beim Umlernen).
  decay: number;
  // Obergrenze fuer den Lern-Bonus, der zu Schicht 1 addiert wird.
  // Sollte in der Skala der search-score-Stufen liegen: Label-Substring
  // = 12, Label-Prefix = 18, Label-Exact = 30. Default 12 = bricht
  // Gleichstaende und hebt mittlere Treffer, ohne echte Namens-Treffer
  // zu ueberstimmen.
  bonusCap: number;
  // Max Anzahl Praefixe in der Lern-Map. Sweet Spot 30-80; mehr macht
  // den persistierten Blob gross, weniger laesst seltenere Begriffe
  // schneller vergessen.
  maxPrefixes: number;
  // Max Items pro Praefix. Mehr = feineres Ranking innerhalb einer
  // Praefix-Gruppe, kostet Speicher quadratisch (Praefixe x Items).
  maxItemsPerPrefix: number;
  // Max Items in der globalen Frecency-Liste (steuert die Default-
  // Vorschlagsliste bei leerer Suche).
  maxGlobalItems: number;
}

export const DEFAULT_CONFIG: LearnConfig = {
  decay: 0.9,
  bonusCap: 12,
  maxPrefixes: 50,
  maxItemsPerPrefix: 10,
  maxGlobalItems: 30,
};

// CONFIG_LIMITS definiert harte Grenzen fuer Settings-UI + Clamping.
// Werte ausserhalb sind entweder bedeutungslos (decay >= 1 = kein Decay,
// score laeuft weg) oder gefaehrlich (maxPrefixes zu hoch = sprengt
// 16-KB-Body-Limit der prefs-API).
export const CONFIG_LIMITS = {
  decay: { min: 0.5, max: 0.99, step: 0.01 },
  bonusCap: { min: 0, max: 60, step: 1 },
  maxPrefixes: { min: 5, max: 100, step: 5 },
  maxItemsPerPrefix: { min: 3, max: 20, step: 1 },
  maxGlobalItems: { min: 0, max: 60, step: 5 },
} satisfies Record<keyof LearnConfig, { min: number; max: number; step: number }>;

export interface LearnState {
  v: typeof STATE_VERSION;
  queries: Record<string, Record<string, number>>; // prefix -> itemId -> score
  global: Record<string, number>;                  // itemId -> frecency-score
}

export interface SearchLearn {
  record(query: string, itemId: string): void;
  bonus(query: string, itemId: string): number;
  topFrecent(n: number): string[];
  serialize(): LearnState;
  isDirty(): boolean;
  clearDirty(): void;
  // setConfig erlaubt Live-Update der Config ohne Neuaufbau des States.
  // Wird vom Store aufgerufen, wenn der User die Settings aendert.
  setConfig(partial: Partial<LearnConfig>): void;
  getConfig(): LearnConfig;
}

function emptyState(): LearnState {
  return { v: STATE_VERSION, queries: {}, global: {} };
}

// clampConfig: zieht User-Werte (oder fehlende Felder) auf die
// CONFIG_LIMITS. Tolerant: nicht-numerische Werte fallen auf den
// Default zurueck.
export function clampConfig(input?: Partial<LearnConfig> | null): LearnConfig {
  const out: LearnConfig = { ...DEFAULT_CONFIG };
  if (!input || typeof input !== "object") return out;
  for (const key of Object.keys(CONFIG_LIMITS) as (keyof LearnConfig)[]) {
    const raw = (input as Record<string, unknown>)[key];
    if (typeof raw !== "number" || !Number.isFinite(raw)) continue;
    const { min, max } = CONFIG_LIMITS[key];
    out[key] = Math.min(max, Math.max(min, raw));
  }
  return out;
}

// normalizeQuery: erstes Wort, lowercased. Leere Query liefert "".
// Wir lernen pro erstem Token, weil das die "Intent"-Granularitaet ist:
// "task" und "task backup" sollen denselben Slot teilen, "host" einen
// anderen. Die Praefix-Variante ist robuster gegen Tipp-Schwankungen
// und haelt die Map klein.
function normalizeQuery(query: string): string {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return "";
  const firstToken = trimmed.split(/\s+/)[0] ?? "";
  return firstToken;
}

function findWeakestKey(map: Record<string, number>): string | undefined {
  let weakestKey: string | undefined;
  let weakestScore = Infinity;
  for (const k in map) {
    const v = map[k];
    if (v < weakestScore) {
      weakestScore = v;
      weakestKey = k;
    }
  }
  return weakestKey;
}

function countKeys(obj: Record<string, unknown>): number {
  let n = 0;
  for (const _k in obj) n++;
  return n;
}

// hydrate: nimmt einen unvalidierten State (z.B. aus JSON) und liefert
// eine saubere Kopie. Tolerant gegenueber Drift: unbekannte Felder
// werden ignoriert, falsche Typen verworfen. Garantiert kein Throw --
// im schlimmsten Fall ein leerer State.
function hydrate(raw: unknown): LearnState {
  if (!raw || typeof raw !== "object") return emptyState();
  const r = raw as Partial<LearnState>;
  if (r.v !== STATE_VERSION) return emptyState();
  const out = emptyState();
  if (r.queries && typeof r.queries === "object") {
    for (const prefix in r.queries) {
      const items = r.queries[prefix];
      if (!items || typeof items !== "object") continue;
      const clean: Record<string, number> = {};
      for (const id in items) {
        const v = items[id];
        if (typeof v === "number" && Number.isFinite(v) && v > 0) clean[id] = v;
      }
      if (countKeys(clean) > 0) out.queries[prefix] = clean;
    }
  }
  if (r.global && typeof r.global === "object") {
    for (const id in r.global) {
      const v = r.global[id];
      if (typeof v === "number" && Number.isFinite(v) && v > 0) out.global[id] = v;
    }
  }
  return out;
}

export function createSearchLearn(
  initialState?: unknown,
  initialConfig?: Partial<LearnConfig> | null,
): SearchLearn {
  const state: LearnState = hydrate(initialState);
  let cfg: LearnConfig = clampConfig(initialConfig);
  let dirty = false;

  function decayBucket(bucket: Record<string, number>, itemId: string): void {
    // Schritt 1: bestehende Scores nach unten decayen (mit Floor, damit
    // Eintraege irgendwann komplett verschwinden statt ewig nahe 0 zu
    // schweben). Schritt 2: das ausgewaehlte Item bekommt +1.
    for (const id in bucket) {
      const next = bucket[id] * cfg.decay;
      if (next < 0.05) delete bucket[id];
      else bucket[id] = next;
    }
    bucket[itemId] = (bucket[itemId] ?? 0) + 1;
  }

  function truncatePrefix(bucket: Record<string, number>): void {
    while (countKeys(bucket) > cfg.maxItemsPerPrefix) {
      const weakest = findWeakestKey(bucket);
      if (!weakest) break;
      delete bucket[weakest];
    }
  }

  function truncateQueries(): void {
    if (countKeys(state.queries) <= cfg.maxPrefixes) return;
    // Praefix-Score = Summe aller Item-Scores im Praefix. So fliegen
    // selten gebrauchte Praefixe raus, nicht zufaellig ein gerade
    // angelegter mit hohem Single-Item-Score.
    let weakestPrefix: string | undefined;
    let weakestSum = Infinity;
    for (const prefix in state.queries) {
      let sum = 0;
      const bucket = state.queries[prefix];
      for (const id in bucket) sum += bucket[id];
      if (sum < weakestSum) {
        weakestSum = sum;
        weakestPrefix = prefix;
      }
    }
    if (weakestPrefix) delete state.queries[weakestPrefix];
  }

  function truncateGlobal(): void {
    while (countKeys(state.global) > cfg.maxGlobalItems) {
      const weakest = findWeakestKey(state.global);
      if (!weakest) break;
      delete state.global[weakest];
    }
  }

  // applyShrink: wenn der User in den Settings die Limits senkt, muss
  // der bestehende State darauf reagieren -- sonst sitzen wir auf einem
  // zu grossen Blob bis zur naechsten Auswahl. Idempotent.
  function applyShrink(): void {
    for (const prefix in state.queries) truncatePrefix(state.queries[prefix]);
    truncateQueries();
    truncateGlobal();
  }

  return {
    record(query, itemId) {
      if (!itemId) return;
      const prefix = normalizeQuery(query);

      // Praefix-spezifische Bindung: leere Query (Klick auf Default-
      // Liste) hat keine Bindung -- wir lernen aber trotzdem global.
      if (prefix) {
        let bucket = state.queries[prefix];
        if (!bucket) {
          bucket = {};
          state.queries[prefix] = bucket;
        }
        decayBucket(bucket, itemId);
        truncatePrefix(bucket);
        truncateQueries();
      }

      // Globale Frecency: bei jeder Auswahl mitziehen, damit die
      // Default-Liste bei leerer Query aussagekraeftig wird.
      if (cfg.maxGlobalItems > 0) {
        decayBucket(state.global, itemId);
        truncateGlobal();
      }

      dirty = true;
    },

    bonus(query, itemId) {
      const prefix = normalizeQuery(query);
      if (!prefix) {
        // Bei leerer Query gibt's keinen Praefix-Bonus -- die Default-
        // Liste sortieren wir separat via topFrecent().
        return 0;
      }
      const bucket = state.queries[prefix];
      if (!bucket) return 0;
      const score = bucket[itemId] ?? 0;
      if (score <= 0) return 0;
      return score < cfg.bonusCap ? score : cfg.bonusCap;
    },

    topFrecent(n) {
      if (n <= 0) return [];
      const ids = Object.keys(state.global);
      ids.sort((a, b) => state.global[b] - state.global[a]);
      return ids.slice(0, n);
    },

    serialize() {
      // Defensive Kopie, damit der Caller den Live-State nicht aus
      // Versehen mutiert (z.B. via JSON.stringify-Patches).
      const out = emptyState();
      for (const prefix in state.queries) {
        const src = state.queries[prefix];
        const dst: Record<string, number> = {};
        for (const id in src) dst[id] = src[id];
        out.queries[prefix] = dst;
      }
      for (const id in state.global) out.global[id] = state.global[id];
      return out;
    },

    isDirty() {
      return dirty;
    },

    clearDirty() {
      dirty = false;
    },

    setConfig(partial) {
      const merged = clampConfig({ ...cfg, ...partial });
      const shrinking =
        merged.maxPrefixes < cfg.maxPrefixes ||
        merged.maxItemsPerPrefix < cfg.maxItemsPerPrefix ||
        merged.maxGlobalItems < cfg.maxGlobalItems;
      cfg = merged;
      if (shrinking) {
        applyShrink();
        dirty = true;
      }
    },

    getConfig() {
      return { ...cfg };
    },
  };
}
